<script setup>
import {computed, nextTick, onBeforeMount, onBeforeUnmount, onMounted, ref, watch} from 'vue'
import {useProductStore} from '@js/store/ProductStore.js'
import MobileTopBar from "@js/components/TopBar/MobileTopBar.vue"
import Breadcrumbs from "@js/components/Breadcrumbs/Breadcrumbs.vue"
import ProductCartForm from "@js/components/Sections/Product/ProductCartForm.vue"
import TabsComponent from "@js/components/Tabs/TabsComponent.vue"
import ProductAllCharacteristics from "@js/components/Sections/Product/Tabs/ProductAllCharacteristics.vue"
import ProductFaq from "@js/components/Sections/Product/Tabs/ProductFaq.vue"
import OnlyDesktop from "@js/components/DynamicComponents/OnlyDesktop.vue";
import OnlyMobile from "@js/components/DynamicComponents/OnlyMobile.vue";
import ImageLoader from "@js/components/Loaders/ImageLoader.vue";
import ProductCharacteristicsFeature from "@js/components/Sections/Product/ProductCharacteristicsFeature.vue";
import IconCreditCard from "@js/components/Icons/IconCreditCard.vue";
import {Link} from "@inertiajs/vue3";
import IconDeliveryCar from "@js/components/Icons/IconDeliveryCar.vue";
import FastBuyCallback from "@js/components/Callbacks/FastBuyCallback.vue";
import {checkDocument} from '@js/helpers.ts';
import SimilarProducts from "@js/components/Sections/Product/SimilarProducts.vue";
import {ecomDetail, mapProduct} from "@js/ecommerce.js";

const props = defineProps({
    product: {
        type: Object,
        required: true,
    },
    characteristics_grouped: {
        type: Object,
        required: false,
    },
})

const productStore = useProductStore()
productStore.product = props.product
productStore.characteristics_grouped = props.characteristics_grouped

const characteristics_grouped = computed(() => productStore.characteristics_grouped ?? [])
const base_chars = computed(() => (characteristics_grouped.value || []).slice(0, 5))

const currentTab = ref(1)
const allCharsRef = ref(null)

const today = new Date(Date.now() + 86_400_000).toLocaleDateString('ru-RU', {
    timeZone: 'Europe/Moscow'
}); // это дата «завтра» по Москве
const tabs = [
    {id: 1, title: 'Все характеристики'},
    {id: 2, title: 'Описание'},
    {id: 3, title: 'Частые вопросы'},
]

function stripHtml(html) {
    const tmp = document.createElement("div")
    tmp.innerHTML = html
    let text = tmp.textContent || tmp.innerText || ""
    return text.replace(/\n/g, " ").replace(/\s+/g, " ").trim()
}

function goToAllChars() {
    // Переключаем вкладку
    currentTab.value = 1
    // После обновления DOM — плавно скроллим
    nextTick(() => {
        if (allCharsRef.value) {
            allCharsRef.value.scrollIntoView({behavior: 'smooth', block: 'center'})
        }
    })
}

onBeforeMount(() => {
    // detail — просмотр карточки товара
    ecomDetail(mapProduct(props.product, { list: 'PDP', position: 1 }))
})
</script>

<template>
    <MobileTopBar/>
    <div class="container">
        <Breadcrumbs/>

        <OnlyMobile :maxSize="1024">
            <h1 class="flex items-center text-2xl md:text-3xl font-bold mb-3">
                {{ productStore.product.title }}
            </h1>
        </OnlyMobile>

        <div class="grid grid-cols-12 gap-y-5 md:gap-y-10 md:gap-x-10 text-gray-900 mb-5">
            <div class="col-span-12 md:col-span-7 lg:col-span-5 order-1 relative">
                <ImageLoader :title="productStore.product.title"
                             :src="'/storage/assets/products/'+productStore.product.category.slug+'/'+ productStore.product.media.images.preview_wide"
                             imgClasses="w-full h-auto object-contain border border-gray-200 rounded-2xl"/>
            </div>
            <div class="col-span-12 lg:col-span-4 order-3 lg:order-2 flex flex-col gap-3">
                <OnlyDesktop :maxSize="1024">
                    <h1 class="flex items-center text-2xl md:text-3xl font-bold mb-0">
                        {{ productStore.product.title }}
                    </h1>
                </OnlyDesktop>
                <div>
                    <div class="text-base font-semibold text-gray-900 mb-2">
                        Характеристики
                    </div>
                    <div class="flex flex-col gap-1">
                        <ProductCharacteristicsFeature v-for="char in base_chars"
                                                       :title="char.title" :description="char.values"/>
                    </div>
                </div>
                <div @click.prevent="goToAllChars" aria-label="Смотреть все характеристики"
                     class="underline text-sm font-medium cursor-pointer md:hover:text-navHoverColor transition">
                    См. все характеристики
                </div>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-2.5 mb-5">
                    <Link :href="route('aboutPayment')"
                          class="flex items-center justify-center text-start gap-3 text-sm w-full bg-transparent border border-dashed border-gray-500 text-black p-3 rounded-lg md:hover:bg-baseColor md:hover:text-white font-semibold transition">
                        <IconCreditCard class="min-w-7 max-w-7 min-h-7 max-h-7"/>
                        Оплата наличными и безналичным
                    </Link>
                    <Link :href="route('deliveryAndShipping')"
                          class="flex items-center justify-center text-start gap-3 text-sm w-full bg-transparent border border-dashed border-gray-500 text-black p-3 rounded-lg md:hover:bg-baseColor md:hover:text-white font-semibold transition">
                        <IconDeliveryCar class="min-w-7 max-w-7 min-h-7 max-h-7"/>
                        Ближайшая дата доставки: {{ today }}
                    </Link>
                </div>
            </div>

            <div class="col-span-12 md:col-span-5 lg:col-span-3 order-2 lg:order-3">
                <ProductCartForm :key="productStore.product.id"/>
            </div>
        </div>

        <div class="shadow-lg rounded-2xl mb-10">
            <TabsComponent v-model="currentTab" :tabs="tabs"/>
            <div class="border border-t-0 rounded-b-2xl p-5">
                <div v-show="currentTab === 1" ref="allCharsRef">
                    <ProductAllCharacteristics/>
                </div>
                <div v-show="currentTab === 2">
                    <div v-if="productStore.product.media.description"
                         class="flex flex-col gap-1 bbp"
                         v-html="productStore.product.media.description"></div>
                    <div v-else class="text-sm text-gray-600 italic">Описания пока нет</div>
                </div>
                <div v-show="currentTab === 3">
                    <ProductFaq :items="productStore.product.media.faq"/>
                </div>
            </div>
        </div>

        <SimilarProducts v-if="productStore.product.id" :id="productStore.product.id"/>
    </div>
    <FastBuyCallback/>
</template>

<style lang="scss">
.bbp {
    /* Кастомизируй при желании через CSS-переменные */
    --bbp-text: #000;
    --bbp-head: #000;
    --bbp-muted: #000;
    --bbp-accent: var(--main-color);
    --bbp-border: #000;

    color: var(--bbp-text);
    font-size: 0.95rem;
    line-height: 1.5;
    word-break: normal;
    overflow-wrap: anywhere;

    > *:first-child {
        margin-top: 0;
    }

    > *:last-child {
        margin-bottom: 0;
    }

    p {
        margin: 0;
    }

    h2, h3 {
        color: var(--bbp-head);
        font-weight: 500;
        line-height: 1.25;
    }

    h2, h3 {
        font-size: clamp(1rem, 0.95rem + 0.4vw, 1.2rem);
    }

    /* Отступы между блоками */
    h2 + ul,
    h3 + ul,
    h3 + p,
    h2 + p {
        margin-top: 0.5em;
    }

    p + h2,
    p + h3,
    ul + h2,
    ul + h3 {
        margin-top: 1em;
    }

    /* Списки — без дефолтных буллитов, со своими маркерами */
    ul {
        list-style: none;
        padding: 0;

        > li {
            position: relative;
            //margin: 0.35em 0;
            padding-left: 1.25em;
        }

        > li::before {
            content: "";
            position: absolute;
            left: 0;
            top: 0.6em;
            width: 0.5em;
            height: 0.5em;
            border-radius: 50%;
            background: var(--bbp-accent);
        }
    }

    /* Мобильная и десктопная подстройка размеров */
    @media (min-width: 768px) {
        ul > li {
            margin: 0.4em 0;
        }
    }

    /* Тёмная схема (если нужна) */
    @media (prefers-color-scheme: dark) {
        --bbp-text: #000; /* gray-200 */
        --bbp-head: #000; /* gray-100 */
        --bbp-muted: #000; /* gray-400 */
        --bbp-border: #000; /* gray-700 */
        --bbp-accent: var(--main-color); /* sky-400 */
    }
}

</style>
